import React from 'react';
import * as styleFunctions from './propStyles'; // Import self so we can reference own functions, see hover()
import { get, isArray, isString, omit } from 'lodash';
import { css } from 'styled-components';

interface StyleParams {
    key?: string;
    prop: string;
    cssProperty: string;
    unit?: string;
}

export const style = (styleParams: StyleParams) => props => {
    const { key, prop, cssProperty, unit } = styleParams;
    // Get value of style prop from props of styled-component
    const propValue = get(props, prop, undefined);

    // If there is no propValue return null
    if (!propValue) {
        return null;
    }

    // If the prop value is an array then generate media query styles
    // It is expected that a theme containing breakpoints is provided to the styled-component
    if (isArray(propValue)) {
        // Get breakpoints from theme
        const breakpoints = get(props, `theme.breakpoints`, []);

        // Create media query for each breakpoint with styles within
        const responsiveStyles = Object.values(breakpoints).map(
            (breakpoint, index) => {
                const value = propValue[index + 1];
                if (value) {
                    return `@media screen and (min-width: ${breakpoint}) {
                            ${cssStyle(
                                props,
                                value,
                                key,
                                prop,
                                cssProperty,
                                unit
                            )}
                        }`;
                }
            }
        );

        // Add "default" style for below min-width breakpoint
        responsiveStyles.unshift(
            cssStyle(props, propValue[0], key, prop, cssProperty, unit)
        );

        return responsiveStyles.join('\n');
    }

    // Else return the style
    return cssStyle(props, propValue, key, prop, cssProperty, unit);
};

export const cssStyle = (props, propValue, key, prop, cssProperty, unit) => {
    // Convert cssProperty to array so we can apply a value to mulitple properties if specfied. E.g: "padding-top, padding-bottom"
    cssProperty = cssProperty.replace(/ /g, '').split(',');

    // Get a value from the theme using key and propValue. E.g: theme.space.xl
    // If this fails because the style function was not provided a key (i.e. not a theme based style),
    // or the propValue does not exist in the theme key then the raw propValue will be used
    let value = get(props, `theme.${key}.${propValue}`, propValue);

    // If the value is not a string, apply unit to value if it was provied to the style function
    // Otherwise, if the value is a string the raw propValue will be used.

    // This allows prop styles to be used in a flexible way, for example:
    // fontSize="md" // font-size will be theme.fontSize.md
    // fontSize="inherit" // font-size will be "inherit"
    // fontSize="1em" // font-size will be 1em
    // fontSize={16} // font-size will be 16 units (px) as specified in fontSize style function

    if (!isString(value)) {
        value = unit ? `${value}${unit}` : value;
    }

    // Return the style
    return cssProperty.map(property => `${[property]}: ${value};`).join('\n');
};

// Selector styles work by overwriting current styled-component props with props contained in another prop named after a selector, i.e hover, active, selected etc. The prop name can actually be anything but it's easier to think about if they match up!
// We then get an array of style functions used, using the prop keys, and return those style functions wrapped in the defined selector.
// Styled-components' css method will handle the interpolation of style functions from the array.
// Styles are applied in conjunction with the selector. For example, if the selector is ".active" the component using the active propStyle must set that class name in the DOM.
// Pseudo classes such as ":hover", ":active" are provided automatically by the browser.
export const selectorStyle = ({ prop, selector }) => props => {
    const selectorStyleProps = get(props, prop, {});
    Object.assign(props, selectorStyleProps);
    const selectorStyleFunctions = Object.keys(
        selectorStyleProps
    ).map(selectorStyleProp => get(styleFunctions, selectorStyleProp));
    return css`
        &${selector} {
            ${selectorStyleFunctions};
        }
    `;
};

// Function to return only the prop style functions that are used.
// Get an array of props, filter all the style functions by that array, then return an array of those functions.
// NOTE: selectorStyle functions must be called last, otherwise their props will take precedence.
export const propStyles = props => {
    const propsArray = Object.keys(props);
    const propStyleFunctions = Object.keys(styleFunctions)
        .filter(fn => propsArray.includes(fn))
        .sort((a, b) =>
            [
                'hover',
                'active',
                'selected',
                'activated',
                'alternated',
                'focus',
            ].includes(a)
                ? 1
                : -1
        )
        .map(fn => get(styleFunctions, fn));
    return css`
        ${propStyleFunctions};
    `;
};

export const hover = selectorStyle({ prop: 'hover', selector: ':hover' });
export const active = selectorStyle({ prop: 'active', selector: ':active' });
export const focus = selectorStyle({ prop: 'focus', selector: ':focus' });
export const selected = selectorStyle({
    prop: 'selected',
    selector: '[data-selected="true"]',
});
export const alternated = selectorStyle({
    prop: 'alternated',
    selector: '[data-alternated="true"]',
});
export const activated = selectorStyle({
    prop: 'activated',
    selector: '.active',
});

export const display = style({
    prop: 'display',
    cssProperty: 'display',
});

export const fontFamily = style({
    prop: 'fontFamily',
    key: 'fontFamily',
    cssProperty: 'font-family',
});

export const fontSize = style({
    prop: 'fontSize',
    key: 'fontSize',
    cssProperty: 'font-size',
    unit: 'px',
});

export const fontWeight = style({
    prop: 'fontWeight',
    key: 'fontWeight',
    cssProperty: 'font-weight',
});

export const fontStyle = style({
    prop: 'fontStyle',
    cssProperty: 'font-style',
});

export const padding = style({
    prop: 'padding',
    key: 'space',
    cssProperty: 'padding',
    unit: 'px',
});

export const paddingX = style({
    prop: 'paddingX',
    key: 'space',
    cssProperty: 'padding-left, padding-right',
    unit: 'px',
});

export const paddingY = style({
    prop: 'paddingY',
    key: 'space',
    cssProperty: 'padding-top, padding-bottom',
    unit: 'px',
});

export const margin = style({
    prop: 'margin',
    key: 'space',
    cssProperty: 'margin',
    unit: 'px',
});

export const marginX = style({
    prop: 'marginX',
    key: 'space',
    cssProperty: 'margin-left, margin-right',
    unit: 'px',
});

export const marginY = style({
    prop: 'marginY',
    key: 'space',
    cssProperty: 'margin-top, margin-bottom',
    unit: 'px',
});

export const lineHeight = style({
    prop: 'lineHeight',
    key: 'lineHeight',
    cssProperty: 'line-height',
});

export const width = style({
    prop: 'width',
    cssProperty: 'width',
    unit: 'px',
});

export const minWidth = style({
    prop: 'minWidth',
    cssProperty: 'min-width',
});

export const maxWidth = style({
    prop: 'maxWidth',
    cssProperty: 'max-width',
});

export const height = style({
    prop: 'height',
    cssProperty: 'height',
    unit: 'px',
});

export const minHeight = style({
    prop: 'minHeight',
    cssProperty: 'min-height',
});

export const maxHeight = style({
    prop: 'maxHeight',
    cssProperty: 'max-height',
});

export const iconSize = style({
    prop: 'iconSize',
    key: 'iconSize',
    cssProperty: 'height, width',
    unit: 'px',
});

export const bgColor = style({
    prop: 'bgColor',
    key: 'color',
    cssProperty: 'background-color',
});

export const color = style({
    prop: 'color',
    key: 'color',
    cssProperty: 'color',
});

export const fill = style({
    prop: 'fill',
    key: 'color',
    cssProperty: 'fill',
});

export const flex = style({
    prop: 'flex',
    cssProperty: 'flex',
});

export const flexDirection = style({
    prop: 'flexDirection',
    cssProperty: 'flex-direction',
});

export const flexWrap = style({
    prop: 'flexWrap',
    cssProperty: 'flex-wrap',
});

export const alignItems = style({
    prop: 'alignItems',
    cssProperty: 'align-items',
});

export const alignSelf = style({
    prop: 'alignSelf',
    cssProperty: 'align-self',
});

export const justifyContent = style({
    prop: 'justifyContent',
    cssProperty: 'justify-content',
});

export const borderWidth = style({
    prop: 'borderWidth',
    cssProperty: 'border-width',
    unit: 'px',
});

export const borderRadius = style({
    prop: 'borderRadius',
    key: 'radii',
    cssProperty: 'border-radius',
    unit: 'px',
});

export const borderColor = style({
    prop: 'borderColor',
    key: 'color',
    cssProperty: 'border-color',
});

export const borderStyle = style({
    prop: 'borderStyle',
    cssProperty: 'border-style',
});

export const boxShadow = style({
    prop: 'boxShadow',
    key: 'shadow',
    cssProperty: 'box-shadow',
});

export const textDecoration = style({
    prop: 'textDecoration',
    cssProperty: 'text-decoration',
});

export const textTransform = style({
    prop: 'textTransform',
    cssProperty: 'text-transform',
});

export const textAlign = style({
    prop: 'textAlign',
    cssProperty: 'text-align',
});

export const textOverflow = style({
    prop: 'textOverflow',
    cssProperty: 'text-overflow',
});

export const wordBreak = style({
    prop: 'wordBreak',
    cssProperty: 'word-break',
});

export const filter = style({
    prop: 'filter',
    cssProperty: 'filter',
});

export const outline = style({
    prop: 'outline',
    cssProperty: 'outline',
});

export const zIndex = style({
    prop: 'zIndex',
    key: 'zIndex',
    cssProperty: 'z-index',
});

export const transform = style({
    prop: 'transform',
    cssProperty: 'transform',
});

export const position = style({
    prop: 'position',
    cssProperty: 'position',
});

export const left = style({
    prop: 'left',
    cssProperty: 'left',
});

export const right = style({
    prop: 'right',
    cssProperty: 'right',
});

export const listStyle = style({
    prop: 'listStyle',
    cssProperty: 'list-style',
});

export const overflow = style({
    prop: 'overflow',
    cssProperty: 'overflow',
});

export const overflowX = style({
    prop: 'overflowX',
    cssProperty: 'overflow-x',
});

export const overflowY = style({
    prop: 'overflowY',
    cssProperty: 'overflow-y',
});

export const order = style({
    prop: 'order',
    cssProperty: 'order',
});

export const background = style({
    prop: 'background',
    cssProperty: 'background',
});

export const cursor = style({
    prop: 'cursor',
    cssProperty: 'cursor',
});

export const transition = style({
    prop: 'transition',
    cssProperty: 'transition',
});

// tslint:disable-next-line:no-shadowed-variable
export const injectStyles = ({ injectStyles }) => injectStyles;

// Prevent style props being passed on to the underlying DOM element to avoid unknown prop warnings
export const cleanProps = Component => {
    return class Cleaned extends React.Component {
        render() {
            const cleanedProps = omit(this.props, Object.keys(styleFunctions));
            return React.createElement(Component, cleanedProps);
        }
    };
};



// WEBPACK FOOTER //
// ./src/utils/propStyles.ts